import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import AuthLayout from "../components/AuthLayout"
import PasswordVisibilityToggle from "../components/PasswordVisibilityToggle"
import { getAPIBaseURL } from "../services/helpers"
import { postData } from "../services/request"
import { validateLoginIdentifier, validateLoginPassword } from "../services/validators"
import {
  parseEmploymeRequestResult,
  LOGIN_INVALID_CREDENTIALS_MSG,
  resolveLoginErrorMessage,
} from "../services/employmeAuthErrors"
import { getReferralAttribution } from "../services/referralAttribution"
import { navigateAfterLogin } from "../services/authNavigation"

const EmployerLogin = () => {
  const navigate = useNavigate()
  const [identifier, setIdentifier] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [btnState, setBtnState] = useState("Sign in")
  const [error, setError] = useState([
    { field: "identifier", msg: "" },
    { field: "password", msg: "" }
  ])
  const [genError, setGenError] = useState("")
  const [info, setInfo] = useState("")

  const validateForm = () => {
    let ok = true
    const errs = [...error]
    const idVal = validateLoginIdentifier(identifier)
    const pVal = validateLoginPassword(password)
    errs[errs.findIndex(i => i.field === "identifier")].msg = idVal.result
    errs[errs.findIndex(i => i.field === "password")].msg = pVal.result
    if (idVal.error !== "") ok = false
    if (pVal.error !== "") ok = false
    setError(errs)
    return ok
  }

  const login = async () => {
    setGenError("")
    setInfo("")
    if (!validateForm()) return
    setBtnState("Loading...")
    const url = `${getAPIBaseURL()}/employme/auth/login`
    const headers = {}
    const payload = { identifier: identifier.trim(), password }
    const referral = getReferralAttribution()
    if (referral) {
      payload.referralCode = referral.referralCode
      payload.campaignId = referral.campaignId
      if (referral.deviceToken) payload.deviceToken = referral.deviceToken
      if (referral.visitorId) payload.visitorId = referral.visitorId
    }
    const requestResult = await postData(url, headers, payload)
    setBtnState("Sign in")

    const { ok, status, message } = parseEmploymeRequestResult(requestResult)

    if (ok && status === 200) {
      try {
        const userData = requestResult.result.data?.data || requestResult.result.data
        await navigateAfterLogin(userData, navigate)
      } catch (e) {
        console.error("Error logging in:", e)
        setGenError("An error occurred during login. Please try again.")
      }
      return
    }

    if (status === 404 || status === 401) {
      setGenError(LOGIN_INVALID_CREDENTIALS_MSG)
    } else if (status === 403 && (message || "").toLowerCase().includes("verify your email")) {
      setInfo(message || "Please verify your email address before logging in.")
    } else {
      setGenError(resolveLoginErrorMessage({ status, message }))
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Sign in to your employer or job seeker account.">
      <div className="space-y-5">
        {genError !== "" ? (
          <div className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-500">{genError}</div>
        ) : null}
        {!genError && info ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{info}</div>
        ) : null}

        <div>
          <label htmlFor="identifier" className="mb-2 block text-sm font-medium text-gray-900">Email or phone</label>
          <input
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            type="text"
            id="identifier"
            className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
            placeholder="name@example.com or phone number"
          />
          {error.find(i => i.field === "identifier")?.msg ? (
            <p className="mt-1 text-sm font-semibold text-red-500">{error.find(i => i.field === "identifier").msg}</p>
          ) : null}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label htmlFor="password" className="text-sm font-medium text-gray-900">Password</label>
            <Link to="/forgot-password" className="text-sm font-medium text-blue-600 hover:underline">Forgot password?</Link>
          </div>
          <div className="relative">
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type={showPassword ? "text" : "password"}
              id="password"
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 pr-10 text-base text-gray-900 focus:border-primary-600 focus:ring-primary-600 sm:text-sm"
              placeholder="••••••••"
              onKeyDown={(e) => e.key === "Enter" && login()}
            />
            <PasswordVisibilityToggle
              visible={showPassword}
              onToggle={() => setShowPassword((prev) => !prev)}
            />
          </div>
          {error.find(i => i.field === "password")?.msg ? (
            <p className="mt-1 text-sm font-semibold text-red-500">{error.find(i => i.field === "password").msg}</p>
          ) : null}
        </div>

        <button onClick={() => login()} type="button" className="w-full rounded-lg bg-[#00A5E6] px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-[#008FCC] focus:outline-none focus:ring-4 focus:ring-[#cceffd]">{btnState}</button>

        <p className="text-sm font-light text-gray-500">
          Don&apos;t have an account?{" "}
          <Link to="/signup" className="font-medium text-blue-600 hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </AuthLayout>
  )
}

export default EmployerLogin
